import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Contract, B2BInvoice } from '@hospasense/contracts';
import { apiGet, apiSend } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { formatDate, relativeTime } from '../../lib/format.js';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  LoadingState,
  Modal,
  Input,
  Textarea,
  useToast,
} from '../../components/ui/index.js';

export function BillingTab({ orgId }: { orgId: string }) {
  const { hasPermission } = useAuth();
  const canRead = hasPermission('contract.read') || hasPermission('billing.read');
  const canManage = hasPermission('contract.manage') || hasPermission('billing.manage');
  const toast = useToast();
  const queryClient = useQueryClient();

  const [issueInvoiceOpen, setIssueInvoiceOpen] = useState(false);
  const [settleInvoice, setSettleInvoice] = useState<B2BInvoice | null>(null);
  const [paymentRef, setPaymentRef] = useState('');
  const [settlementNote, setSettlementNote] = useState('');

  // Invoice creation form state
  const [newInvoiceAmount, setNewInvoiceAmount] = useState('30000');
  const [newInvoiceNotes, setNewInvoiceNotes] = useState('');
  const [newInvoiceRecipient, setNewInvoiceRecipient] = useState('');

  // Fetch active contract
  const contractQuery = useQuery({
    queryKey: ['org-contract', orgId],
    queryFn: () =>
      apiGet<{ contract: Contract | null }>(`/api/v1/commercial/organisations/${orgId}/contract`),
    enabled: canRead,
  });

  // Fetch invoices for this organisation
  const invoicesQuery = useQuery({
    queryKey: ['org-invoices', orgId],
    queryFn: () =>
      apiGet<{ items: B2BInvoice[]; total: number }>(
        `/api/v1/billing/invoices?organisationId=${orgId}`,
      ),
    enabled: canRead,
  });

  // Settle invoice mutation
  const settleMutation = useMutation({
    mutationFn: ({ invoiceId, ref, note }: { invoiceId: string; ref: string; note: string }) =>
      apiSend<B2BInvoice>('POST', `/api/v1/billing/invoices/${invoiceId}/settle`, {
        paymentReference: ref,
        notes: note || undefined,
      }),
    onSuccess: (updated) => {
      toast.success(`Invoice ${updated.invoiceNumber} marked as settled / paid`);
      queryClient.invalidateQueries({ queryKey: ['org-invoices', orgId] });
      setSettleInvoice(null);
      setPaymentRef('');
      setSettlementNote('');
    },
    onError: (err: Error) => {
      toast.error(`Settlement failed: ${err.message}`);
    },
  });

  // Create invoice mutation
  const createInvoiceMutation = useMutation({
    mutationFn: async () => {
      const contract = contractQuery.data?.contract;
      if (!contract) throw new Error('No active contract found for this organisation');

      const subtotal = Number(newInvoiceAmount);
      const gstAmount = Math.round(subtotal * 0.18);
      const totalAmount = subtotal + gstAmount;

      return apiSend<B2BInvoice>('POST', '/api/v1/billing/invoices', {
        organisationId: orgId,
        contractId: contract.id,
        subtotalInr: subtotal,
        discountInr: 0,
        gstTax: {
          cgstInr: Math.round(gstAmount / 2),
          sgstInr: Math.round(gstAmount / 2),
          igstInr: 0,
          ratePercent: 18,
        },
        totalAmountInr: totalAmount,
        currency: 'INR',
        billingPeriodStart: new Date().toISOString(),
        billingPeriodEnd: new Date(Date.now() + 30 * 86400000).toISOString(),
        issueDate: new Date().toISOString(),
        dueDate: new Date(Date.now() + 15 * 86400000).toISOString(),
        recipientEmail: newInvoiceRecipient || 'billing@hospasense.com',
        customerGstin: contract.gstin || undefined,
        lineItems: [
          {
            description: `${contract.planName} · Subscription Term`,
            amountInr: subtotal,
            quantity: 1,
            rateInr: subtotal,
          },
        ],
        notes: newInvoiceNotes || undefined,
      });
    },
    onSuccess: (inv) => {
      toast.success(`Invoice ${inv.invoiceNumber} generated successfully`);
      queryClient.invalidateQueries({ queryKey: ['org-invoices', orgId] });
      setIssueInvoiceOpen(false);
      setNewInvoiceAmount('30000');
      setNewInvoiceNotes('');
    },
    onError: (err: Error) => {
      toast.error(`Invoice generation failed: ${err.message}`);
    },
  });

  if (!canRead) {
    return (
      <EmptyState
        title="Access Restricted"
        description="Your profile does not have permission to view billing data."
      />
    );
  }

  if (contractQuery.isLoading || invoicesQuery.isLoading) {
    return <LoadingState label="Loading commercial contract & billing ledger…" />;
  }

  const contract = contractQuery.data?.contract;
  const invoices = invoicesQuery.data?.items ?? [];

  return (
    <div className="space-y-6">
      {/* 1. Active Commercial Contract Section */}
      <Card>
        <CardHeader
          title="Active Commercial Contract"
          subtitle={
            contract
              ? `${contract.contractNumber} · ${contract.planName} (${contract.billingInterval.toUpperCase()})`
              : 'No active contract currently on file'
          }
          actions={
            contract ? (
              <span className="inline-flex items-center rounded-full bg-success-bg px-3 py-1 text-xs font-semibold text-success capitalize">
                ● {contract.status.replace('_', ' ')}
              </span>
            ) : undefined
          }
        />
        <CardBody>
          {contract ? (
            <div className="space-y-6">
              {/* Top Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="rounded-lg bg-bg-subtle p-3 border border-border">
                  <div className="text-xs text-text-muted">Annual Value (ACV)</div>
                  <div className="text-lg font-bold text-text-primary mt-1">
                    ₹{(contract.annualContractValueInr || 0).toLocaleString('en-IN')}
                  </div>
                  <div className="text-[11px] text-text-secondary mt-0.5">
                    ₹
                    {Math.round((contract.annualContractValueInr || 0) / 12).toLocaleString(
                      'en-IN',
                    )}{' '}
                    / mo equiv.
                  </div>
                </div>

                <div className="rounded-lg bg-bg-subtle p-3 border border-border">
                  <div className="text-xs text-text-muted">Billing Cycle</div>
                  <div className="text-lg font-bold text-text-primary capitalize mt-1">
                    {contract.billingInterval}
                  </div>
                  <div className="text-[11px] text-text-secondary mt-0.5">
                    {contract.autoRenew ? 'Auto-renews automatically' : 'Manual renewal required'}
                  </div>
                </div>

                <div className="rounded-lg bg-bg-subtle p-3 border border-border">
                  <div className="text-xs text-text-muted">Contract Validity</div>
                  <div className="text-sm font-semibold text-text-primary mt-1">
                    {formatDate(contract.startDate)} — {formatDate(contract.endDate)}
                  </div>
                  <div className="text-[11px] text-text-muted mt-0.5">
                    {relativeTime(contract.endDate)}
                  </div>
                </div>

                <div className="rounded-lg bg-bg-subtle p-3 border border-border">
                  <div className="text-xs text-text-muted">Tax & Jurisdiction</div>
                  <div className="text-sm font-semibold text-text-primary mt-1 truncate">
                    State: {contract.jurisdictionStateCode || 'KA'}
                  </div>
                  <div className="text-[11px] text-text-secondary mt-0.5 truncate">
                    GSTIN: {contract.gstin || 'Unregistered / SEZ'}
                  </div>
                </div>
              </div>

              {/* Entitlements & Included Modules */}
              <div className="grid md:grid-cols-2 gap-4 pt-2">
                <div className="rounded-lg border border-border p-4 bg-surface space-y-2">
                  <div className="text-xs font-bold text-text-primary uppercase tracking-wider">
                    Licensed Modules
                  </div>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {(contract.includedModules || []).length > 0 ? (
                      (contract.includedModules as string[]).map((mod) => (
                        <span
                          key={mod}
                          className="rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
                        >
                          {mod.replace(/_/g, ' ')}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-text-muted">
                        Full Core Clinical & Administrative Suite
                      </span>
                    )}
                  </div>
                </div>

                <div className="rounded-lg border border-border p-4 bg-surface space-y-2">
                  <div className="text-xs font-bold text-text-primary uppercase tracking-wider">
                    Capacity Entitlements
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs pt-1">
                    <div className="flex justify-between border-b border-border/50 pb-1">
                      <span className="text-text-muted">Doctor Seats:</span>
                      <span className="font-semibold text-text-primary">Licensed per plan</span>
                    </div>
                    <div className="flex justify-between border-b border-border/50 pb-1">
                      <span className="text-text-muted">Bed Capacity:</span>
                      <span className="font-semibold text-text-primary">Active census quota</span>
                    </div>
                    <div className="flex justify-between border-b border-border/50 pb-1">
                      <span className="text-text-muted">Notice Period:</span>
                      <span className="font-semibold text-text-primary">
                        {contract.renewalNoticeDays || 30} days
                      </span>
                    </div>
                    <div className="flex justify-between border-b border-border/50 pb-1">
                      <span className="text-text-muted">Grace Period:</span>
                      <span className="font-semibold text-text-primary">
                        {contract.gracePeriodDays || 15} days
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <EmptyState
              title="No commercial contract attached"
              description="To assign a plan or rate card, create a commercial contract in the Commercial Management console."
            />
          )}
        </CardBody>
      </Card>

      {/* 2. B2B Invoices & Ledger Table */}
      <Card>
        <CardHeader
          title="Invoices & Payment Ledger"
          subtitle="All platform billing statements, payment receipts, and tax invoice records"
          actions={
            canManage && contract ? (
              <Button size="sm" onClick={() => setIssueInvoiceOpen(true)}>
                Generate Invoice
              </Button>
            ) : undefined
          }
        />
        <CardBody>
          {invoices.length === 0 ? (
            <EmptyState
              title="No invoices on record"
              description="Generated B2B platform invoices and settlement receipts will appear here."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="border-b border-border bg-surface-muted/50 text-xs font-semibold uppercase tracking-wider text-text-muted">
                    <th className="py-3 px-4">Invoice #</th>
                    <th className="py-3 px-4">Issue Date</th>
                    <th className="py-3 px-4">Billing Period</th>
                    <th className="py-3 px-4">Total Amount (INR)</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Settlement Ref</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="hover:bg-surface-hover/50 transition-colors">
                      <td className="py-3 px-4 font-mono font-medium text-text-primary">
                        {inv.invoiceNumber}
                      </td>
                      <td className="py-3 px-4 text-xs text-text-muted">
                        <div>{formatDate(inv.issueDate)}</div>
                        <div className="text-[10px] opacity-75">Due {formatDate(inv.dueDate)}</div>
                      </td>
                      <td className="py-3 px-4 text-xs text-text-muted">
                        {formatDate(inv.billingPeriodStart)} — {formatDate(inv.billingPeriodEnd)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-bold text-text-primary">
                          ₹{(inv.totalAmountInr || 0).toLocaleString('en-IN')}
                        </div>
                        <div className="text-[10px] text-text-muted">
                          Subtotal ₹{(inv.subtotalInr || 0).toLocaleString('en-IN')} + GST
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {inv.status === 'paid' ? (
                          <span className="inline-flex items-center rounded-full bg-success-bg px-2.5 py-0.5 text-xs font-semibold text-success">
                            Paid
                          </span>
                        ) : inv.status === 'overdue' ? (
                          <span className="inline-flex items-center rounded-full bg-error-bg px-2.5 py-0.5 text-xs font-semibold text-error">
                            Overdue
                          </span>
                        ) : inv.status === 'in_grace' ? (
                          <span className="inline-flex items-center rounded-full bg-warning-bg px-2.5 py-0.5 text-xs font-semibold text-warning">
                            In Grace
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-surface-muted px-2.5 py-0.5 text-xs font-semibold text-text-muted capitalize">
                            {inv.status}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-xs">
                        {inv.paymentReference ? (
                          <div>
                            <span className="font-mono text-text-primary">
                              {inv.paymentReference}
                            </span>
                            {inv.paidAt && (
                              <div className="text-[10px] text-text-muted">
                                {formatDate(inv.paidAt)}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-text-muted italic">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {inv.status !== 'paid' && canManage && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              setSettleInvoice(inv);
                              setPaymentRef('');
                              setSettlementNote('');
                            }}
                          >
                            Record Payment
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Modal: Generate B2B Invoice */}
      {issueInvoiceOpen && contract && (
        <Modal
          open={true}
          title={`Generate Invoice for ${contract.planName}`}
          onClose={() => setIssueInvoiceOpen(false)}
        >
          <div className="space-y-4">
            <p className="text-sm text-text-muted">
              Creates and issues a statutory B2B tax invoice against contract{' '}
              <strong>{contract.contractNumber}</strong> with 18% GST calculation.
            </p>

            <Input
              label="Billing Subtotal (INR before GST)"
              type="number"
              required
              value={newInvoiceAmount}
              onChange={(e) => setNewInvoiceAmount(e.target.value)}
              hint={`Total with 18% GST: ₹${Math.round(Number(newInvoiceAmount || 0) * 1.18).toLocaleString('en-IN')}`}
            />

            <Input
              label="Recipient Billing Email"
              type="email"
              placeholder="e.g. accounts@denzclinic.com"
              value={newInvoiceRecipient}
              onChange={(e) => setNewInvoiceRecipient(e.target.value)}
            />

            <Textarea
              label="Invoice Notes & Bank Account Details"
              rows={2}
              value={newInvoiceNotes}
              onChange={(e) => setNewInvoiceNotes(e.target.value)}
              placeholder="e.g. SenseSys Health Technologies Pvt Ltd — HDFC Bank NEFT/RTGS"
            />

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setIssueInvoiceOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={createInvoiceMutation.isPending || Number(newInvoiceAmount) <= 0}
                onClick={() => void createInvoiceMutation.mutate()}
              >
                {createInvoiceMutation.isPending ? 'Issuing…' : 'Issue Invoice'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal: Record Payment Settlement */}
      {settleInvoice && (
        <Modal
          open={true}
          title={`Record Payment for Invoice ${settleInvoice.invoiceNumber}`}
          onClose={() => setSettleInvoice(null)}
        >
          <div className="space-y-4">
            <p className="text-sm text-text-muted">
              Mark this invoice of{' '}
              <strong>₹{(settleInvoice.totalAmountInr || 0).toLocaleString('en-IN')}</strong> as
              paid and record the banking or gateway reference.
            </p>

            <Input
              label="UTR / Payment Reference *"
              required
              placeholder="e.g. UTR-HDFC-982736412 or Razorpay pay_xyz"
              value={paymentRef}
              onChange={(e) => setPaymentRef(e.target.value)}
            />

            <Textarea
              label="Settlement Notes"
              rows={2}
              value={settlementNote}
              onChange={(e) => setSettlementNote(e.target.value)}
              placeholder="e.g. Received via NEFT to SenseSys corporate account"
            />

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setSettleInvoice(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={settleMutation.isPending || !paymentRef.trim()}
                onClick={() =>
                  void settleMutation.mutate({
                    invoiceId: settleInvoice.id,
                    ref: paymentRef.trim(),
                    note: settlementNote.trim(),
                  })
                }
              >
                {settleMutation.isPending ? 'Recording…' : 'Confirm Settlement'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
